<template>
  <div id="nomination-conference-edit" class="premium-dialog-wrapper">
    <v-card class="modal-main-card">
      <!-- Premium Sticky Glass Header -->
      <div
        class="modal-header-bar d-flex align-center justify-space-between px-6 py-4"
      >
        <div class="d-flex align-center">
          <v-avatar color="rgba(255,255,255,0.2)" size="42" class="mr-3">
            <v-icon color="white">mdi-file-document-edit-outline</v-icon>
          </v-avatar>
          <div>
            <h2 class="modal-header-title">
              {{ PageTitle || "Edit Nomination Conference" }}
            </h2>
            <span class="modal-header-subtitle"
              >Update conference dates, withdrawal rules & election officer
              info</span
            >
          </div>
        </div>
        <v-btn icon dark class="close-btn" @click="closePrompt">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-form ref="form1" v-model="valid1" lazy-validation id="createForm">
        <v-card-text class="modal-body pa-6">
          <!-- Loading overlay -->
          <div class="d-flex justify-content-center my-4" v-if="LoadingFlag">
            <b-spinner
              variant="primary"
              type="grow"
              label="Loading..."
            ></b-spinner>
          </div>

          <v-container v-if="StartupLoadingFlag">
            <v-row wrap>
              <v-col cols="12" md="4" v-for="n in 6" :key="n">
                <v-skeleton-loader type="paragraph"></v-skeleton-loader>
              </v-col>
            </v-row>
          </v-container>

          <v-row wrap v-if="ProgessStart == 1" class="mb-4">
            <v-col align="center" cols="12">
              <v-progress-circular
                :rotate="180"
                :size="80"
                :width="10"
                :value="uploadPercentage"
                color="#1565c0"
              >
                {{ uploadPercentage }}%
              </v-progress-circular>
              <p class="text-subtitle-2 text-danger mt-2">
                Updating details, please wait...
              </p>
            </v-col>
          </v-row>

          <div v-if="!StartupLoadingFlag">
            <!-- SECTION 1: ORGANIZATION & LO DETAILS -->
            <v-card class="form-section-card mb-6" elevation="0">
              <div class="section-card-header">
                <v-icon color="#1565c0" class="mr-2">mdi-domain</v-icon>
                <span>Organization & LO Selection</span>
              </div>
              <v-card-text class="pa-5">
                <v-row wrap>
                  <!-- Zone -->
                  <v-col cols="12" sm="6" md="6">
                    <label class="form-field-label">
                      <span class="text-danger">*</span> Zone
                    </label>
                    <v-autocomplete
                      :reduce="(option) => option.value"
                      v-model="ZoneCode"
                      :rules="ZoneCodeRules"
                      :items="ZoneCodeOptions"
                      :loading="ZoneCodeOptionsLoading"
                      placeholder="Select Zone"
                      dense
                      clearable
                      required
                      outlined
                      rounded
                      class="premium-input-field"
                    ></v-autocomplete>
                  </v-col>

                  <!-- LO -->
                  <v-col cols="12" sm="6" md="6">
                    <label class="form-field-label">
                      <b>LO</b> (Optional)
                    </label>
                    <v-autocomplete
                      :reduce="(option) => option.value"
                      :loading="LomCodeOptionsLoading"
                      :items="LomCodeOptions"
                      :rules="LomCodeRules"
                      v-model="LomCode"
                      placeholder="Select LO"
                      outlined
                      dense
                      clearable
                      rounded
                      class="premium-input-field"
                    ></v-autocomplete>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- SECTION 2: CONFERENCE & NOMINATION DATES -->
            <v-card class="form-section-card mb-6" elevation="0">
              <div class="section-card-header">
                <v-icon color="#1565c0" class="mr-2">mdi-calendar-clock</v-icon>
                <span>Conference Schedules & Bidding Deadlines</span>
              </div>
              <v-card-text class="pa-5">
                <v-row wrap>
                  <!-- Conference From Date -->
                  <v-col cols="12" sm="6" md="3">
                    <label class="form-field-label">
                      <span class="text-danger">*</span> Conference Start Date
                    </label>
                    <v-menu
                      v-model="ConferenceFromMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          required
                          v-model="ConferenceFromDate"
                          placeholder="Select Date"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          dense
                          outlined
                          rounded
                          class="premium-input-field"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="ConferenceFromDate"
                        @input="ConferenceFromMenu = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>

                  <!-- Conference To Date -->
                  <v-col cols="12" sm="6" md="3">
                    <label class="form-field-label">
                      <span class="text-danger">*</span> Conference End Date
                    </label>
                    <v-menu
                      v-model="ConferenceToMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="ConferenceToDate"
                          placeholder="Select Date"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          dense
                          outlined
                          rounded
                          class="premium-input-field"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="ConferenceToDate"
                        @input="ConferenceToMenu = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>

                  <!-- Nomination Conference From Date -->
                  <v-col cols="12" sm="6" md="3">
                    <label class="form-field-label">
                      <span class="text-danger">*</span> Nomination Start Date
                    </label>
                    <v-datetime-picker
                      v-model="NominationConfFromDate"
                      :time-picker-props="timeProps"
                      time-format="HH:mm:ss"
                      prepend-icon="mdi-calendar-clock"
                      class="premium-datetime-picker"
                    ></v-datetime-picker>
                  </v-col>

                  <!-- Nomination Conference To Date -->
                  <v-col cols="12" sm="6" md="3">
                    <label class="form-field-label">
                      <span class="text-danger">*</span> Nomination End Date
                    </label>
                    <v-datetime-picker
                      v-model="NominationConfToDate"
                      :time-picker-props="timeProps"
                      time-format="HH:mm:ss"
                      prepend-icon="mdi-calendar-clock"
                      class="premium-datetime-picker"
                    ></v-datetime-picker>
                  </v-col>

                  <!-- Award From Date -->
                  <v-col cols="12" sm="6" md="3">
                    <label class="form-field-label">
                      <span class="text-danger">*</span> Award Bidding Start
                      Date
                    </label>
                    <v-datetime-picker
                      v-model="AwardFromDate"
                      :time-picker-props="timeProps"
                      time-format="HH:mm:ss"
                      prepend-icon="mdi-calendar-clock"
                      class="premium-datetime-picker"
                    ></v-datetime-picker>
                  </v-col>

                  <!-- Award To Date -->
                  <v-col cols="12" sm="6" md="3">
                    <label class="form-field-label">
                      <span class="text-danger">*</span> Award Bidding End Date
                    </label>
                    <v-datetime-picker
                      v-model="AwardToDate"
                      :time-picker-props="timeProps"
                      time-format="HH:mm:ss"
                      prepend-icon="mdi-calendar-clock"
                      class="premium-datetime-picker"
                    ></v-datetime-picker>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- SECTION 3: WITHDRAWAL & ZVP CONFIGURATION -->
            <v-card class="form-section-card mb-6" elevation="0">
              <div class="section-card-header">
                <v-icon color="#1565c0" class="mr-2">mdi-tune-vertical</v-icon>
                <span>Withdrawal & ZVP Rules Configuration</span>
              </div>
              <v-card-text class="pa-5">
                <v-row wrap>
                  <!-- Conference Withdrawal End Date -->
                  <v-col cols="12" sm="6" md="3">
                    <label class="form-field-label">
                      <span class="text-danger">*</span> Withdrawal End Date
                    </label>
                    <v-datetime-picker
                      v-model="WithdrawalEndDate"
                      :time-picker-props="timeProps"
                      time-format="HH:mm:ss"
                      prepend-icon="mdi-calendar-clock"
                      class="premium-datetime-picker"
                    ></v-datetime-picker>
                  </v-col>

                  <!-- Floor Nomination Withdrawal End Date -->
                  <v-col cols="12" sm="6" md="3">
                    <label class="form-field-label">
                      <span class="text-danger">*</span> Floor Nomination
                      Withdrawal End Date
                    </label>
                    <v-datetime-picker
                      v-model="FloorNominationWithdrawalEndDate"
                      :time-picker-props="timeProps"
                      time-format="HH:mm:ss"
                      prepend-icon="mdi-calendar-clock"
                      class="premium-datetime-picker"
                    ></v-datetime-picker>
                  </v-col>

                  <!-- ZP Co-Option End Date -->
                  <v-col cols="12" sm="6" md="3">
                    <label class="form-field-label"> ZP Co-Option End Date </label>
                    <v-datetime-picker
                      v-model="CoOptionEndDate"
                      :time-picker-props="timeProps"
                      time-format="HH:mm:ss"
                      prepend-icon="mdi-calendar-clock"
                      class="premium-datetime-picker"
                    ></v-datetime-picker>
                  </v-col>

                  <!-- ZVP Co-Option End Date -->
                  <v-col cols="12" sm="6" md="3">
                    <label class="form-field-label"> ZVP Co-Option End Date </label>
                    <v-datetime-picker
                      v-model="ZvpCoOptionEndDate"
                      :time-picker-props="timeProps"
                      time-format="HH:mm:ss"
                      prepend-icon="mdi-calendar-clock"
                      class="premium-datetime-picker"
                    ></v-datetime-picker>
                  </v-col>

                  <!-- ZVP Count -->
                  <v-col cols="12" sm="6" md="3">
                    <label class="form-field-label">
                      <span class="text-danger">*</span> ZVP Count
                    </label>
                    <v-text-field
                      v-model="ZvpCount"
                      :rules="[(v) => !!v || 'ZVP Count is required']"
                      type="number"
                      placeholder="Enter ZVP Count"
                      dense
                      outlined
                      required
                      clearable
                      rounded
                      prepend-inner-icon="mdi-counter"
                      class="premium-input-field"
                    ></v-text-field>
                  </v-col>

                  <!-- Withdrawal Extension Max Count -->
                  <v-col cols="12" sm="6" md="3">
                    <label class="form-field-label">
                      <span class="text-danger">*</span> Withdrawal Extension
                      Max Count
                    </label>
                    <v-text-field
                      v-model="WithdrawalExtendMaxCount"
                      :rules="[
                        (v) =>
                          !!v || 'Withdrawal Extension Max Count is required',
                      ]"
                      type="number"
                      placeholder="Enter Extension Max Count"
                      dense
                      outlined
                      required
                      clearable
                      rounded
                      prepend-inner-icon="mdi-numeric"
                      class="premium-input-field"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>
        </v-card-text>

        <!-- Sticky Footer Action -->
        <div class="modal-footer-bar pa-4 d-flex justify-center">
          <v-btn
            @click.prevent="confirmAlert"
            :loading="SubmitFlag"
            rounded
            large
            color="primary"
            class="submit-action-btn font-weight-bold px-10 white--text"
          >
            <v-icon left>mdi-check-circle</v-icon>
            Update Conference Settings
          </v-btn>
          <v-btn
            @click.prevent="closePrompt"
            rounded
            large
            outlined
            class="ml-3 font-weight-bold"
          >
            Cancel
          </v-btn>
        </div>
      </v-form>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import UploadSelectedMemberDocument from "@/view/pages/members/UploadSelectedMemberDocument.vue";
import UploadSelectedMemberPhoto from "@/view/pages/members/UploadSelectedMemberPhoto.vue";

export default {
  mixins: [common],
  components: {
    UploadSelectedMemberDocument,
    UploadSelectedMemberPhoto,
  },
  props: {
    addRecordPrompt: {
      type: Boolean,
      required: true,
    },
    pageDescription: {
      type: String,
    },
    recordData: {
      type: Object,
    },
  },
  data() {
    return {
      valid1: true,
      timeProps: { useSeconds: true, format: "24hr" },

      search: "",
      selected: [],
      tableSelectFlag1: false,
      SubmitFlag: false,
      ResultFlag: false,

      ConferenceFromDate: "",
      ConferenceToDate: "",
      ConferenceFromMenu: false,
      ConferenceToMenu: false,

      NominationConfFromDate: "",
      NominationConfToDate: "",
      NominationConfFromMenu: false,
      NominationConfToMenu: false,

      AwardFromDate: "",
      AwardToDate: "",
      AwardFromMenu: false,
      AwardToMenu: false,

      WithdrawalEndDate: "",
      FloorNominationWithdrawalEndDate: "",
      CoOptionEndDate: "",
      ZvpCoOptionEndDate: "",
      ZvpCount: "",
      WithdrawalExtendMaxCount: "",

      member: {},

      NominationDesignationOptions: [],
      NominationDesignationRules: [
        (v) => !!v || "Nomination Designation is required",
      ],
      NominationDesignationOptionsLoading: false,
      NominationDesignation: "",

      memberDocumentPrompt: false,
      memberPhotoPrompt: false,

      rows: [],
      tableColumns1: [],
      records: [],

      alert: {},

      ProgessStart: 0,
      uploadPercentage: 0,

      SuccessMessageTxtFlag: 0,
      SuccessMessageTxt: "",

      ErrorMessageTxtFlag: 0,
      ErrorMessageTxt: "",

      ResultFlag: false,
      LoadingFlag: false,
      StartupLoadingFlag: false,

      AllMembers: [],

      LomCodeRules: [],
      LomCode: "",
      LomCodeOptions: [],
      LomCodeOptionsLoading: false,

      MemberId: "",
      MemberIdOptions: [],
      MemberIdOptionsLoading: false,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",

      ZoneCodeRules: [],
      ZoneCode: "",
      ZoneCodeOptions: [],
      ZoneCodeOptionsLoading: false,
    };
  },
  computed: {},
  watch: {
    ZoneCodeOptions: function () {
      this.LoadingFlag = false;
      this.ZoneCodeOptionsLoading = false;
    },
    ZoneCode: function () {
      this.LomCode = "";
      this.getLomCodeOptions();
    },
    LomCodeOptions: function () {
      this.LoadingFlag = false;
      this.LomCodeOptionsLoading = false;
    },
    PageInfo: function () {
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      if (tr && Object.keys(tr).length > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.JciYearCode = parseInt(tr.CurrentJciYearId);
        this.YearName = parseInt(tr.CurrentJciYearName);
        this.pageData();
      }
    },
    rows: function () {
      if (this.rows && this.rows.length > 0) {
        this.records = this.rows[0];
      }
    },
  },
  methods: {
    refreshPageData() {
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "conference_settings",
        Action: "edit_2",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
      this.pageData();
    },
    pageData() {
      this.getZoneCodeOptions();
      this.getConferenceRecords();
    },
    getZoneCodeOptions() {
      var yearCode = this.JciYearCode;
      if (yearCode != "") {
        this.LoadingFlag = true;
        this.ZoneCodeOptionsLoading = true;
        var selectbox1_source = "ZoneCode";
        var selectbox1_destination = "ZoneCodeOptions";
        var selectbox1_url = "api/year-wise-lom/zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          YearCode: yearCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getLomCodeOptions() {
      var YearCode = this.JciYearCode;
      var ZoneCode = this.ZoneCode;
      if (YearCode != "" && ZoneCode != "") {
        this.LoadingFlag = true;
        this.LomCodeOptionsLoading = true;
        var selectbox1_source = "LomCode";
        var selectbox1_destination = "LomCodeOptions";
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          YearCode: YearCode,
          ZoneCode: ZoneCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getConferenceRecords() {
      var ConferenceId = this.recordData.ConferenceId;
      if (!ConferenceId) return;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token") || 0;
      var add_url = server_url + "api/nomination-conference/show";
      var upload = {
        UserInterface: 1,
        Conference: ConferenceId,
      };

      const thisIns = this;
      this.LoadingFlag = true;
      this.StartupLoadingFlag = true;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.LoadingFlag = false;
          thisIns.StartupLoadingFlag = false;
          var output = response.data.output;
          var flag = response.data.flag;
          var records = response.data.records;

          if (flag == 1) {
            thisIns.ConferenceToDate = records.ConferenceEndDate;
            thisIns.ConferenceFromDate = records.ConferenceStartDate;
            thisIns.NominationConfFromDate = records.NominationStartDate;
            thisIns.NominationConfToDate = records.NominationEndDate;
            thisIns.AwardFromDate = records.AwardStartDate;
            thisIns.AwardToDate = records.AwardEndDate;
            thisIns.LomCode = records.LomId;
            thisIns.ZoneCode = records.ZoneId;
            thisIns.WithdrawalEndDate =
              records.WithdrawalDateTime ||
              records.WithdrawalEndDate ||
              records.conference_withdrawal_end_date ||
              "";
            thisIns.ZvpCount =
              records.ZvpCount || records.conference_zvp_count || "";
            thisIns.WithdrawalExtendMaxCount =
              records.WithdrawalExtendMaxCount ||
              records.conference_withdrawal_extention_max_count ||
              "";
            thisIns.FloorNominationWithdrawalEndDate =
              records.FloorNominationWithdrawalEndDate ||
              records.floor_nomination_withdrawal_end_date ||
              "";
            thisIns.CoOptionEndDate =
              records.CoOptionEndDate ||
              records.conference_co_option_end_date ||
              "";
            thisIns.ZvpCoOptionEndDate =
              records.ZvpCoOptionEndDate ||
              records.conference_zvp_co_option_end_date ||
              "";
            thisIns.MemberId = records.MembershipId || records.Membership || "";
            if (thisIns.MemberId) {
              thisIns.getMemberRecords();
            }
            thisIns.toast("success", output);
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.LoadingFlag = false;
          thisIns.StartupLoadingFlag = false;
        });
    },
    getMemberRecords() {
      var MemberId = this.MemberId;
      if (!MemberId) return;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token") || 0;
      var add_url = server_url + "api/nominate-member-subscription/info";
      var upload = {
        UserInterface: 2,
        Membership: MemberId,
      };

      const thisIns = this;
      this.LoadingFlag = true;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.LoadingFlag = false;
          if (response.data.flag == 1) {
            thisIns.member = response.data.records;
            thisIns.toast("success", response.data.output);
          } else {
            thisIns.toast("error", response.data.output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.LoadingFlag = false;
        });
    },
    closePrompt() {
      this.$emit("hideEditRecordPrompt", this.ResultFlag);
    },
    confirmAlert() {
      var validate1 = this.$refs.form1.validate();
      if (validate1) {
        Swal.fire({
          title: "Are you sure?",
          text: "Do you want to update this nomination conference configuration?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Save`,
          confirmButtonColor: "#1565c0",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          if (result.isConfirmed) {
            this.submitForm();
          }
        });
      } else {
        this.toast("error", "Kindly fill the required fields", true);
      }
    },
    formatDateTime(val) {
      if (!val) return "";
      if (val instanceof Date) {
        const pad = (n) => String(n).padStart(2, "0");
        return `${val.getFullYear()}-${pad(val.getMonth() + 1)}-${pad(
          val.getDate()
        )} ${pad(val.getHours())}:${pad(val.getMinutes())}:${pad(
          val.getSeconds()
        )}`;
      }
      let s = String(val).trim().replace("T", " ");
      s = s.replace(/(\.\d+)?Z?$/, "").trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s} 00:00:00`;
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(s)) return `${s}:00`;
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) return s;
      const d = new Date(s);
      if (!isNaN(d.getTime())) {
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
          d.getDate()
        )} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      }
      return s;
    },
    submitForm() {
      var ConferenceId = this.recordData.ConferenceId;
      var result = this.$refs.form1.validate();

      if (result && ConferenceId) {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token") || 0;
        var add_url = server_url + "api/nomination-conference/update";
        var upload = {
          UserInterface: 1,
          Conference: ConferenceId,
          Lom: this.LomCode,
          Zone: this.ZoneCode,
          ConferenceStartDate: this.ConferenceFromDate,
          ConferenceEndDate: this.ConferenceToDate,
          NominationStartDate: this.formatDateTime(this.NominationConfFromDate),
          NominationEndDate: this.formatDateTime(this.NominationConfToDate),
          AwardBiddingStartDate: this.formatDateTime(this.AwardFromDate),
          AwardBiddingEndDate: this.formatDateTime(this.AwardToDate),
          WithdrawalEndDate: this.formatDateTime(this.WithdrawalEndDate),
          conference_withdrawal_end_date: this.formatDateTime(
            this.WithdrawalEndDate
          ),
          ZvpCount: this.ZvpCount,
          conference_zvp_count: this.ZvpCount,
          WithdrawalExtendMaxCount: this.WithdrawalExtendMaxCount,
          conference_withdrawal_extention_max_count:
            this.WithdrawalExtendMaxCount,
          FloorNominationWithdrawalEndDate: this.formatDateTime(
            this.FloorNominationWithdrawalEndDate
          ),
          CoOptionEndDate: this.formatDateTime(this.CoOptionEndDate),
          conference_co_option_end_date: this.formatDateTime(
            this.CoOptionEndDate
          ),
          ZvpCoOptionEndDate: this.formatDateTime(this.ZvpCoOptionEndDate),
          conference_zvp_co_option_end_date: this.formatDateTime(
            this.ZvpCoOptionEndDate
          ),
          MembershipId: this.MemberId,
        };

        this.LoadingFlag = true;
        const thisIns = this;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        })
          .then(function (response) {
            thisIns.LoadingFlag = false;
            var output = response.data.output;
            var flag = response.data.flag;

            if (flag == 1) {
              thisIns.ResultFlag = true;
              thisIns.closePrompt();
              thisIns.sweetAlert("success", output, true);
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "";
        if (!ConferenceId) {
          message += "Invalid Conference Record. ";
        }
        if (message !== "") {
          this.sweetAlert("error", message, false);
        }
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap");

.premium-dialog-wrapper {
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif;
  background: #f6f8fc;
}

.modal-main-card {
  border-radius: 0 !important;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f6f8fc !important;
}

.modal-header-bar {
  background: linear-gradient(135deg, #1565c0 0%, #1a73e8 50%, #42a5f5 100%);
  color: white;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 4px 20px rgba(21, 101, 192, 0.25);
}

.modal-header-title {
  font-size: 20px;
  font-weight: 800;
  line-height: 1.2;
}

.modal-header-subtitle {
  font-size: 12px;
  opacity: 0.9;
}

.form-section-card {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(21, 101, 192, 0.08) !important;
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03) !important;
}

.section-card-header {
  background: linear-gradient(
    90deg,
    rgba(21, 101, 192, 0.06) 0%,
    rgba(66, 165, 245, 0.02) 100%
  );
  padding: 12px 20px;
  border-bottom: 1.5px solid rgba(21, 101, 192, 0.08);
  font-size: 15px;
  font-weight: 700;
  color: #1565c0;
  display: flex;
  align-items: center;
}

.form-field-label {
  font-size: 12px;
  font-weight: 700;
  color: #455a64;
  margin-bottom: 6px;
  display: block;
}

.premium-input-field >>> .v-input__control > .v-input__slot {
  border-radius: 12px !important;
  border: 1.5px solid rgba(21, 101, 192, 0.12) !important;
  background-color: white !important;
  transition: all 0.3s ease !important;
}

.premium-input-field >>> .v-input__control > .v-input__slot:hover {
  border-color: rgba(21, 101, 192, 0.35) !important;
}

.member-identity-card {
  background: linear-gradient(
    135deg,
    rgba(21, 101, 192, 0.04) 0%,
    rgba(66, 165, 245, 0.08) 100%
  ) !important;
  border: 1.5px solid rgba(21, 101, 192, 0.15) !important;
  border-radius: 16px !important;
}

.member-name-title {
  font-size: 18px;
  font-weight: 800;
  color: #1565c0;
}

.member-id-tag {
  font-size: 14px;
  font-weight: 600;
  color: #78909c;
}

.modal-footer-bar {
  background: white;
  border-top: 1px solid rgba(21, 101, 192, 0.08);
  position: sticky;
  bottom: 0;
  z-index: 90;
}

.submit-action-btn {
  background: linear-gradient(135deg, #1565c0 0%, #1e88e5 100%) !important;
  box-shadow: 0 6px 20px rgba(21, 101, 192, 0.35) !important;
}

.submit-action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(21, 101, 192, 0.45) !important;
}
</style>
